import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { TGetEntryHistoryParams, TGetEntryHistoryResult } from '@/typings/api/twitter_instantwin_entry_histories.type'

export type TwitterInstantwinEntryHistoryState = {
  error: boolean
  total: number
  loading: boolean
  entryHistories: TGetEntryHistoryResult[]
  params: TGetEntryHistoryParams
}

const createInitialState = (): TwitterInstantwinEntryHistoryState => ({
  error: false,
  entryHistories: [],
  total: 0,
  loading: false,
  params: {
    groupId: 0,
    accountId: 0,
    isWinner: 0,
    sortBy: '-entryDatetime',
    pagingNo: 1,
    limitCount: 25
  }
})

export const useTwitterInstantwinEntryHistoryStore = defineStore('twitterInstantwinEntryHistoryStore', () => {
  const initialState = createInitialState()

  const error = ref<TwitterInstantwinEntryHistoryState['error']>(initialState.error)
  const total = ref<TwitterInstantwinEntryHistoryState['total']>(initialState.total)
  const loading = ref<TwitterInstantwinEntryHistoryState['loading']>(initialState.loading)
  const entryHistories = ref<TwitterInstantwinEntryHistoryState['entryHistories']>(initialState.entryHistories)
  const params = ref<TwitterInstantwinEntryHistoryState['params']>(initialState.params)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    error.value = nextState.error
    total.value = nextState.total
    loading.value = nextState.loading
    entryHistories.value = nextState.entryHistories
    params.value = nextState.params
  }

  const fetchEntryHistory = async (): Promise<void> => {
    if (params.value.groupId === 0) {
      return
    }

    error.value = false
    loading.value = true

    const result = await api.get('/twitter_instantwin_entry_histories', {
      params: params.value
    })

    loading.value = false

    if (!result.data) {
      error.value = true
      return
    }

    entryHistories.value = result.data.entryHistories
    total.value = result.data.dataCount
  }

  const setParams = (newParams: Partial<TGetEntryHistoryParams>): void => {
    params.value = {
      ...params.value,
      ...newParams,
      pagingNo: newParams.pagingNo || 1
    }
  }

  return {
    error: readonly(error),
    total: readonly(total),
    loading: readonly(loading),
    entryHistories: readonly(entryHistories),
    params: readonly(params),
    reset,
    fetchEntryHistory,
    setParams
  }
})
